/**
 * Content Block Parser Utility
 *
 * Splits assistant message text into renderable segments:
 *   - "proposal" - detected by markdown structure (multiple proposal-like headings).
 *   - "chart"    - detected by fenced ```orgain-chart code blocks emitted by the
 *                  backend chat LLM with a JSON ChartSpec payload.
 *   - "text"     - anything else.
 */

import type { ProposalSpec, ProposalSection } from '@/components/ui/InlineChatProposal';

export type { ProposalSpec, ProposalSection };

/**
 * Shape of the JSON payload the chat LLM emits inside an `orgain-chart` fence.
 * Mirrors the documented contract in the backend chat system prompts
 * (see orgain/kb/services/chat_service.py and orgain/kb/agents/chat_agent.py).
 */
export interface ChartSpec {
  type: 'bar' | 'line' | 'pie' | 'area' | 'scatter';
  title?: string;
  xKey?: string;
  series?: Array<{ dataKey: string; name?: string; color?: string }>;
  data: Array<Record<string, string | number>>;
}

export type ContentSegment =
  | { type: 'text'; content: string }
  | { type: 'proposal'; spec: ProposalSpec }
  | { type: 'chart'; spec: ChartSpec };

/**
 * Proposal-like heading keywords used by the markdown proposal detector.
 * If a message contains 3+ of these as markdown headings, it's treated
 * as a proposal and rendered via InlineChatProposal.
 */
const PROPOSAL_HEADING_KEYWORDS = [
  'executive summary', 'scope of work', 'pricing', 'timeline', 'schedule',
  'terms', 'conditions', 'acceptance', 'site details', 'deliverables',
  'introduction', 'overview', 'services', 'methodology', 'payment',
  'warranty', 'exclusions', 'assumptions', 'service frequency',
  'additional services', 'emergency services',
];

/**
 * Attempts to detect a markdown-formatted proposal and convert it to a ProposalSpec.
 * Returns null if the content doesn't look like a proposal.
 *
 * Detects patterns like:
 *   # Proposal Title
 *   ## Executive Summary
 *   content...
 *   ## Scope of Work
 *   content...
 */
function detectMarkdownProposal(content: string): { preText: string; spec: ProposalSpec } | null {
  // Find all ## headings (level 2 or 3)
  const headingRegex = /^#{2,3}\s+(.+)$/gm;
  const headings: { text: string; index: number }[] = [];
  let hMatch: RegExpExecArray | null;
  while ((hMatch = headingRegex.exec(content)) !== null) {
    headings.push({ text: hMatch[1].trim(), index: hMatch.index });
  }

  // Count how many headings match proposal keywords
  const matchCount = headings.filter(h =>
    PROPOSAL_HEADING_KEYWORDS.some(kw => h.text.toLowerCase().includes(kw))
  ).length;

  // Need at least 3 proposal-like section headings to consider it a proposal
  if (matchCount < 3 || headings.length < 3) return null;

  // Extract title from # level-1 heading if present
  const titleMatch = content.match(/^#\s+(.+)$/m);
  const title = titleMatch ? titleMatch[1].trim() : undefined;

  // Extract client from content patterns like "Client: ABC Corp" or "**Client:** ABC Corp"
  const clientMatch = content.match(/\*?\*?Client\*?\*?:\s*(.+)/i);
  const client = clientMatch ? clientMatch[1].trim() : undefined;

  // Extract date
  const dateMatch = content.match(/\*?\*?Date\*?\*?:\s*(.+)/i);
  const date = dateMatch ? dateMatch[1].trim() : undefined;

  // Extract proposal number
  const propNumMatch = content.match(/\*?\*?Proposal\s*(?:#|Number|No\.?)\*?\*?:\s*(.+)/i);
  const proposalNumber = propNumMatch ? propNumMatch[1].trim() : undefined;

  // Build sections from headings
  const sections: ProposalSection[] = [];
  for (let i = 0; i < headings.length; i++) {
    const start = headings[i].index + content.slice(headings[i].index).indexOf('\n') + 1;
    const end = i + 1 < headings.length ? headings[i + 1].index : content.length;
    const sectionContent = content.slice(start, end).trim();

    // Strip markdown formatting for cleaner PDF output
    const cleanContent = sectionContent
      .replace(/\*\*([^*]+)\*\*/g, '$1')  // Remove bold markers
      .replace(/^\s*[-*]\s+/gm, '• ')      // Normalize bullet points
      .trim();

    if (cleanContent) {
      // Remove numbered prefix like "1." or "2." from heading text
      const heading = headings[i].text.replace(/^\d+\.\s*/, '');
      sections.push({ heading, content: cleanContent });
    }
  }

  if (sections.length < 3) return null;

  // Text before the first heading (or before the title heading) is the preText
  const firstHeadingIndex = titleMatch
    ? titleMatch.index!
    : headings[0].index;
  const preText = content.slice(0, firstHeadingIndex).trim();

  return {
    preText,
    spec: {
      title: title || 'Landscape Service Proposal',
      client,
      date,
      proposalNumber,
      sections,
    },
  };
}

/**
 * Minimal structural validation of a parsed ChartSpec payload. We accept any
 * unknown chart `type` strings at parse time but normalise/validate them at
 * render time - here we just guard against obviously broken JSON shapes so a
 * bad fence falls through to text instead of crashing the message renderer.
 */
function isValidChartSpec(value: unknown): value is ChartSpec {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.type !== 'string') return false;
  if (!Array.isArray(v.data)) return false;
  return true;
}

/**
 * Matches a fenced code block whose info string is exactly `orgain-chart`.
 * Tolerates trailing whitespace on the opening line and an optional trailing
 * newline before the closing fence. Captures the raw JSON body.
 */
const ORGAIN_CHART_FENCE_RE = /```orgain-chart[ \t]*\r?\n([\s\S]*?)```/g;

/**
 * Splits a string into alternating text / chart segments by scanning for
 * `orgain-chart` fenced code blocks. Malformed JSON inside a fence is
 * preserved verbatim as text so the user can still see what came through.
 */
function splitTextByChartFences(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;
  // Reset state on the shared regex before each scan.
  ORGAIN_CHART_FENCE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ORGAIN_CHART_FENCE_RE.exec(content)) !== null) {
    const fullMatch = match[0];
    const jsonBody = match[1];
    const matchStart = match.index;

    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonBody);
    } catch {
      parsed = null;
    }

    if (isValidChartSpec(parsed)) {
      // Emit preceding text (if any) then the chart segment.
      const preceding = content.slice(lastIndex, matchStart);
      if (preceding.length > 0) {
        segments.push({ type: 'text', content: preceding });
      }
      segments.push({ type: 'chart', spec: parsed });
      lastIndex = matchStart + fullMatch.length;
    }
    // If invalid: leave the fence in place; the surrounding text segment
    // will include it verbatim when we emit the trailing slice below.
  }

  const trailing = content.slice(lastIndex);
  if (trailing.length > 0 || segments.length === 0) {
    segments.push({ type: 'text', content: trailing });
  }
  return segments;
}

/**
 * Splits message content into text, proposal, and chart segments.
 *
 * Order of detection:
 *   1. Markdown proposal - preserves the existing behaviour for proposal-shaped
 *      assistant messages.
 *   2. orgain-chart fences - extracted from any remaining text segments.
 *
 * A message can contain both prose and one or more chart fences; surrounding
 * text becomes its own text segment(s).
 */
export function splitContentByCharts(content: string): ContentSegment[] {
  const baseSegments: ContentSegment[] = [];
  const mdProposal = detectMarkdownProposal(content);
  if (mdProposal) {
    if (mdProposal.preText) {
      baseSegments.push({ type: 'text', content: mdProposal.preText });
    }
    baseSegments.push({ type: 'proposal', spec: mdProposal.spec });
  } else {
    baseSegments.push({ type: 'text', content });
  }

  // Expand any text segments into text + chart segments, preserving order.
  const expanded: ContentSegment[] = [];
  for (const seg of baseSegments) {
    if (seg.type === 'text') {
      expanded.push(...splitTextByChartFences(seg.content));
    } else {
      expanded.push(seg);
    }
  }
  return expanded;
}
